/**
 * Shared antivirus + backup status pills used by both the Customers list
 * (CustomersModule) and the Documentation → Organization Detail page.
 *
 * Backend stores per-record:
 *   antivirus: string | null
 *   backups: [{ type, status, last_updated_at }]
 *
 * Status options are fixed in code (NOT user-editable) so badge colours and
 * reporting stay consistent across the app.
 */
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ShieldCheck,
  HardDrive,
  AlertTriangle,
  X as XIcon,
  CheckCircle2,
  Plus,
  Trash2,
} from 'lucide-react';

export const BACKUP_STATUS_OPTIONS = [
  { value: 'successful', label: 'Successful', tone: 'emerald' },
  { value: 'warning', label: 'Successful with warnings', tone: 'amber' },
  { value: 'failed', label: 'Failed', tone: 'red' },
];

const BACKUP_STATUS_BY_VALUE = Object.fromEntries(
  BACKUP_STATUS_OPTIONS.map((o) => [o.value, o]),
);

const STATUS_TONE_CLASSES = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

export function BackupStatusBadge({ status }) {
  const opt = BACKUP_STATUS_BY_VALUE[status] || BACKUP_STATUS_OPTIONS[0];
  const Icon = opt.value === 'failed' ? XIcon : opt.value === 'warning' ? AlertTriangle : CheckCircle2;
  return (
    <span className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border text-[11px] font-medium ${STATUS_TONE_CLASSES[opt.tone]}`}>
      <Icon className="w-3 h-3" />
      {opt.label}
    </span>
  );
}

/** Compact "x ago" formatter — keeps the badge short while still conveying staleness. */
function formatAgo(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
}

/**
 * Read-only pills row: antivirus (cyan) + one pill per backup (slate/status).
 * `entityId` is used to make data-testids unique when multiple records render
 * on the same page.
 */
export function ClientStatusPills({ entity, entityId }) {
  const av = entity?.antivirus;
  const backups = Array.isArray(entity?.backups) ? entity.backups : [];
  if (!av && backups.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid={`client-status-pills-${entityId}`}>
      {av && (
        <span
          className="inline-flex items-center gap-1 px-2 h-6 rounded-full border bg-cyan-50 text-cyan-700 border-cyan-200 text-[11px] font-medium"
          data-testid={`client-antivirus-${entityId}`}
        >
          <ShieldCheck className="w-3 h-3" />
          {av}
        </span>
      )}
      {backups.map((b, i) => {
        const ago = formatAgo(b.last_updated_at);
        return (
          <span
            key={`${b.type}-${i}`}
            className="inline-flex items-center gap-1 px-2 h-6 rounded-full border bg-slate-50 text-slate-700 border-slate-200 text-[11px] font-medium"
            data-testid={`client-backup-${entityId}-${i}`}
            title={ago ? `Last updated ${ago}` : undefined}
          >
            <HardDrive className="w-3 h-3" />
            {b.type}
            <span className="text-slate-400">·</span>
            <BackupStatusBadge status={b.status} />
            {ago && <span className="text-slate-400">· {ago}</span>}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Shared editor used inside the Customer + Organization create/edit dialogs.
 * Antivirus dropdown + repeatable Backups rows. Status options are fixed.
 *
 * Props:
 *   data: { antivirus, backups }
 *   onChange: (patch) => void
 *   antivirusOptions / backupTypeOptions: string[] from /api/categories
 *   testIdPrefix: string  (e.g. "create-customer", "edit-org")
 */
export function ClientAVBackupsFields({
  data,
  onChange,
  antivirusOptions,
  backupTypeOptions,
  testIdPrefix,
}) {
  const backups = Array.isArray(data?.backups) ? data.backups : [];

  const updateRow = (idx, patch) => {
    const next = backups.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    onChange({ backups: next });
  };

  const addRow = () => {
    const defaultType = backupTypeOptions[0] || '';
    onChange({ backups: [...backups, { type: defaultType, status: 'successful' }] });
  };

  const removeRow = (idx) => {
    onChange({ backups: backups.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-cyan-600" />
        <Label className="text-slate-700 m-0">Antivirus &amp; Backups</Label>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-slate-600">Antivirus</Label>
        <Select
          value={data.antivirus || '__none__'}
          onValueChange={(v) => onChange({ antivirus: v === '__none__' ? '' : v })}
        >
          <SelectTrigger className="bg-white border-slate-200" data-testid={`${testIdPrefix}-antivirus-select`}>
            <SelectValue placeholder="Select antivirus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {antivirusOptions.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500">Add more options under Settings → Categories → Antivirus.</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-600 m-0">Backups</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-full"
            onClick={addRow}
            disabled={backupTypeOptions.length === 0}
            data-testid={`${testIdPrefix}-backup-add-btn`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add backup
          </Button>
        </div>

        {backups.length === 0 ? (
          <p className="text-[11px] text-slate-500">No backups configured. Click "Add backup" to track one.</p>
        ) : (
          <div className="space-y-2">
            {backups.map((b, idx) => {
              const ago = formatAgo(b.last_updated_at);
              return (
                <div key={idx} className="space-y-1" data-testid={`${testIdPrefix}-backup-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <Select value={b.type || ''} onValueChange={(v) => updateRow(idx, { type: v })}>
                        <SelectTrigger className="bg-white border-slate-200 h-9" data-testid={`${testIdPrefix}-backup-type-${idx}`}>
                          <SelectValue placeholder="Backup type" />
                        </SelectTrigger>
                        <SelectContent>
                          {backupTypeOptions.map((o) => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-6">
                      <Select value={b.status || 'successful'} onValueChange={(v) => updateRow(idx, { status: v })}>
                        <SelectTrigger className="bg-white border-slate-200 h-9" data-testid={`${testIdPrefix}-backup-status-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BACKUP_STATUS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-slate-500 hover:text-red-600"
                        onClick={() => removeRow(idx)}
                        data-testid={`${testIdPrefix}-backup-remove-${idx}`}
                        aria-label="Remove backup"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {ago && (
                    <p className="text-[10px] text-slate-400 pl-1" data-testid={`${testIdPrefix}-backup-ago-${idx}`}>
                      Last updated {ago}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-slate-500">Add more backup types under Settings → Categories → Backup.</p>
      </div>
    </div>
  );
}
